import {
  ImageProcessingError,
  MIN_IMAGE_DIMENSION,
  type ImageProcessingErrorCode,
} from './imageProcessor';
import { MediaUploadClientError } from '../models/MediaUploadClientError';

export type MediaUploadTranslateFn = (key: string, options?: Record<string, unknown>) => string;

const IMAGE_PROCESSING_CODES = new Set<ImageProcessingErrorCode>([
  'UNSUPPORTED_MIME_TYPE',
  'FILE_TOO_LARGE',
  'INVALID_IMAGE',
  'IMAGE_TOO_SMALL',
  'IMAGE_PROCESSING_FAILED',
]);

function isImageProcessingErrorLike(
  error: unknown,
): error is { name: string; code: ImageProcessingErrorCode } {
  if (!error || typeof error !== 'object') return false;
  const rec = error as { name?: unknown; code?: unknown };
  return (
    rec.name === 'ImageProcessingError' &&
    typeof rec.code === 'string' &&
    IMAGE_PROCESSING_CODES.has(rec.code as ImageProcessingErrorCode)
  );
}

export function getMediaUploadFriendlyMessage(error: unknown, t: MediaUploadTranslateFn): string {
  if (error instanceof ImageProcessingError || isImageProcessingErrorLike(error)) {
    const code = error instanceof ImageProcessingError ? error.code : error.code;
    switch (code) {
      case 'UNSUPPORTED_MIME_TYPE':
        return t('mediaUpload.errors.unsupportedMimeType');
      case 'FILE_TOO_LARGE':
        return t('mediaUpload.errors.fileTooLarge');
      case 'IMAGE_TOO_SMALL':
        return t('mediaUpload.errors.imageTooSmall', { minSize: MIN_IMAGE_DIMENSION });
      case 'INVALID_IMAGE':
        return t('mediaUpload.errors.invalidImage');
      case 'IMAGE_PROCESSING_FAILED':
        return t('mediaUpload.errors.processingFailed');
      default:
        return t('mediaUpload.errors.processingFailed');
    }
  }
  if (error instanceof MediaUploadClientError) {
    return t('mediaUpload.errors.detail', { message: error.message });
  }
  if (error instanceof Error && error.name === 'MediaUploadClientError') {
    return t('mediaUpload.errors.detail', { message: error.message });
  }
  if (error instanceof DOMException && error.name === 'AbortError') {
    return t('mediaUpload.errors.requestTimedOut');
  }
  if (error instanceof Error && error.name === 'AbortError') {
    return t('mediaUpload.errors.requestTimedOut');
  }
  if (error instanceof TypeError) {
    return t('mediaUpload.errors.networkUnavailable');
  }
  return t('mediaUpload.errors.genericUploadFailed');
}
